import { STATUS } from "@/constants/statuses";
import RewardsSpendingRulesStore from "@apps/rewards/stores/RewardsSpendingRulesStore";
import {
    rewardsApplicationLoggerToken,
    rewardsAuthApiClientToken,
} from "@apps/rewards/tokens";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import { JsonMimeType } from "@lib/ApiClient";
import type ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import {
    Observable,
    catchError,
    finalize,
    lastValueFrom,
    map,
    tap,
} from "rxjs";
import { create } from "superstruct";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import type { RewardsSpendingRulesQueryParams } from "./query_params/RewardsSpendingRulesQueryParams";
import type {
    RewardsSpendingRulesRedeemResponseType,
    RewardsSpendingRulesResponseType,
} from "./response/RewardsSpendingRulesResponseSchema";
import {
    RewardsSpendingRulesRedeemResponseSchema,
    RewardsSpendingRulesResponseSchema,
} from "./response/RewardsSpendingRulesResponseSchema";

@singleton()
class RewardsSpendingRulesApiService {
    constructor(
        private rewardsSpendingRulesStore: RewardsSpendingRulesStore,
        @inject(rewardsAuthApiClientToken)
        private readonly apiClient: ApiClientInterface,
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getSpendingRules(
        queryParams: RewardsSpendingRulesQueryParams
    ): Observable<RewardsSpendingRulesResponseType> {
        this.logger.debug(
            "RewardsSpendingRulesApiService.getSpendingRules is started"
        );

        const status = this.rewardsSpendingRulesStore.getStatus();
        this.rewardsSpendingRulesStore.setStatus(getLoadingStatus(status));

        return this.apiClient
            .get({
                url: "/points/getSpendingRules",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) =>
                    create(response.body, RewardsSpendingRulesResponseSchema)
                ),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsSpendingRulesApiService.getSpendingRules is errored",
                        error
                    );
                    this.rewardsSpendingRulesStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsSpendingRulesApiService.getSpendingRules is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.rewardsSpendingRulesStore.update((state) => ({
                            ...state,
                            spendingRulesList: value.rules,
                        }));
                        this.rewardsSpendingRulesStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    redeem(params: {
        ruleId: number;
        selectedVariantId?: number;
        spendPoints?: number;
    }): Observable<RewardsSpendingRulesRedeemResponseType> {
        this.logger.debug("RewardsSpendingRulesApiService.redeem is started");

        this.rewardsSpendingRulesStore.setLoading(true);

        return this.apiClient
            .post({
                url: "/points/redeem",
                body: JSON.stringify(params),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) =>
                    create(
                        response.body,
                        RewardsSpendingRulesRedeemResponseSchema
                    )
                ),
                finalize(() => {
                    this.rewardsSpendingRulesStore.setLoading(false);
                }),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsSpendingRulesApiService.redeem is errored",
                        { params, error }
                    );
                    this.rewardsSpendingRulesStore.setError(true);
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "RewardsSpendingRulesApiService.redeem is ready",
                        params
                    );
                })
            );
    }

    //Use this method when two or more widgets can be injected on the same page to avoid multiple requests
    public async checkRequestStatusAndGetSpendingRules(
        queryParams: RewardsSpendingRulesQueryParams
    ): Promise<void> {
        if (this.rewardsSpendingRulesStore.getStatus() === STATUS.VIRGIN)
            await lastValueFrom(this.getSpendingRules(queryParams));
    }
}

export default RewardsSpendingRulesApiService;
